/**
 * On-screen instruction overlay.
 *
 * Displays a game-show-styled banner at the TOP of the stage telling viewers
 * what to do at each phase. Uses fit-text so the prompt never overflows.
 *
 * Also: persistent help strip at the bottom listing chat commands.
 */

import { fitText } from './fit-text.js';

const TOP_ID = 'sg-instructions-top';
const BOTTOM_ID = 'sg-instructions-bottom';

export interface InstructionsHandle {
  topEl: HTMLDivElement;
  bottomEl: HTMLDivElement;
  setInstruction: (label: string, sublabel?: string, variant?: Variant) => void;
  hideInstruction: () => void;
  setCommands: (cmds: string[]) => void;
  dispose: () => void;
}

export type Variant = 'default' | 'vote' | 'answer' | 'celebrate';

const STYLES = `
  @keyframes sg-instr-in {
    from { opacity: 0; transform: translate(-50%, -14px); }
    to   { opacity: 1; transform: translate(-50%, 0);     }
  }
  @keyframes sg-instr-pulse {
    0%, 100% { box-shadow: 0 0 0   rgba(255,196,61,0.55), 0 6px 40px rgba(0,0,0,0.5); }
    50%      { box-shadow: 0 0 32px rgba(255,196,61,0.85), 0 6px 40px rgba(0,0,0,0.5); }
  }
  @keyframes sg-answer-pulse {
    0%, 100% { box-shadow: 0 0 0   rgba(61,239,255,0.55), 0 6px 40px rgba(0,0,0,0.5); }
    50%      { box-shadow: 0 0 32px rgba(61,239,255,0.95), 0 6px 40px rgba(0,0,0,0.5); }
  }
  .sg-instr-top {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 60;
    pointer-events: none;
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    /* Grown per viewer feedback: banner is now the primary "what to do" cue
       since chat announces have been trimmed to bookends. Wider min + more
       padding + taller label/sub rows below. Stays centered, still fits 1920×1080. */
    min-width: 560px;
    max-width: 72%;
    padding: 14px 34px 16px 34px;
    background: linear-gradient(180deg, rgba(6,12,233,0.93) 0%, rgba(3,4,40,0.96) 100%);
    border: 3px solid var(--sg-primary, #FFC43D);
    border-radius: 16px;
    color: #fff;
    text-align: center;
    animation: sg-instr-in 260ms ease-out;
    box-shadow: 0 8px 36px rgba(0,0,0,0.6);
  }
  .sg-instr-top.hidden { display: none; }
  .sg-instr-top.variant-vote      { animation: sg-instr-in 260ms ease-out, sg-instr-pulse 1.8s ease-in-out infinite 260ms; border-color: var(--sg-primary, #FFC43D); }
  .sg-instr-top.variant-answer    { animation: sg-instr-in 260ms ease-out, sg-answer-pulse 1.2s ease-in-out infinite 260ms; border-color: var(--sg-accent, #3DEFFF); }
  .sg-instr-top.variant-celebrate { border-color: var(--sg-primary, #FFC43D); background: linear-gradient(180deg, rgba(255,196,61,0.22) 0%, rgba(6,12,233,0.9) 100%); }

  .sg-instr-label {
    /* Bumped 32→44px (~37%) so the prompt is legible from across a living-room
       couch. Height + fit-text bounds raised to match. */
    font-size: 44px;
    letter-spacing: 0.08em;
    line-height: 1.05;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 3px 10px rgba(0,0,0,0.7), 0 0 22px rgba(255,196,61,0.45);
    overflow: hidden;
    height: 50px;
    display: flex; align-items: center; justify-content: center;
  }
  .sg-instr-sub {
    font-family: 'Inter', system-ui, sans-serif;
    /* Bumped 16→22px (~37%) proportional to the label. */
    font-size: 22px;
    margin-top: 6px;
    color: #E2E8F0;
    opacity: 0.95;
    overflow: hidden;
    height: 30px;
    display: flex; align-items: center; justify-content: center;
  }
  .sg-instr-sub code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.14);
    padding: 3px 10px;
    border-radius: 5px;
    font-family: 'Inter', ui-monospace, monospace;
    font-size: 0.95em;
  }

  @keyframes sg-help-pulse {
    0%   { box-shadow: 0 0 0 rgba(255,196,61,0); border-color: rgba(255,196,61,0.25); transform: translateX(-50%) scale(1.0); }
    35%  { box-shadow: 0 0 38px rgba(255,196,61,0.85); border-color: rgba(255,196,61,0.95); transform: translateX(-50%) scale(1.04); }
    100% { box-shadow: 0 0 0 rgba(255,196,61,0); border-color: rgba(255,196,61,0.25); transform: translateX(-50%) scale(1.0); }
  }
  .sg-instr-bottom {
    position: absolute;
    bottom: 18px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 60;
    pointer-events: none;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 16px;
    color: #CBD5E1;
    background: rgba(3,4,40,0.6);
    border: 1px solid var(--sg-primary, #FFC43D);
    border-radius: 999px;
    padding: 8px 22px;
    letter-spacing: 0.04em;
    animation: sg-help-pulse 1500ms ease-out 800ms 1;
  }
  .sg-instr-bottom code {
    color: var(--sg-primary, #FFC43D);
    font-family: ui-monospace, monospace;
    font-size: 0.95em;
    margin: 0 3px;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('sg-instructions-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-instructions-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createInstructions(mount: HTMLElement): InstructionsHandle {
  ensureStyles();

  const topEl = document.createElement('div');
  topEl.id = TOP_ID;
  topEl.className = 'sg-instr-top hidden';
  topEl.innerHTML = `
    <div class="sg-instr-label" data-role="label"></div>
    <div class="sg-instr-sub"   data-role="sub"></div>
  `;
  mount.appendChild(topEl);

  const labelEl = topEl.querySelector<HTMLDivElement>('[data-role="label"]')!;
  const subEl = topEl.querySelector<HTMLDivElement>('[data-role="sub"]')!;

  const bottomEl = document.createElement('div');
  bottomEl.id = BOTTOM_ID;
  bottomEl.className = 'sg-instr-bottom';
  bottomEl.innerHTML = '';
  mount.appendChild(bottomEl);

  function setInstruction(label: string, sublabel?: string, variant: Variant = 'default'): void {
    topEl.classList.remove('hidden', 'variant-vote', 'variant-answer', 'variant-celebrate');
    topEl.classList.add(`variant-${variant}`);
    labelEl.textContent = label;
    subEl.innerHTML = sublabel ?? '';
    // Fit after mounting; ResizeObserver handles subsequent window changes.
    // max bounds raised in lockstep with the CSS bumps above so fitText
    // doesn't clamp the prompt back down to the old size on long strings.
    requestAnimationFrame(() => {
      fitText(labelEl, { min: 18, max: 50 });
      if (sublabel) fitText(subEl, { min: 12, max: 26 });
    });
  }

  function hideInstruction(): void {
    topEl.classList.add('hidden');
  }

  function setCommands(cmds: string[]): void {
    if (cmds.length === 0) {
      bottomEl.style.display = 'none';
      return;
    }
    bottomEl.style.display = '';
    bottomEl.innerHTML = cmds.map((c) => `<code>${escapeHtml(c)}</code>`).join(' ');
  }

  return {
    topEl,
    bottomEl,
    setInstruction,
    hideInstruction,
    setCommands,
    dispose() {
      topEl.remove();
      bottomEl.remove();
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
